// Shared helpers of the releases page (spec/dashboard.md section 12.2): cache keys, the dry-run result shape, the job
// tones, the issue-list check of a failed build's `error.details`, pretty JSON; the deploy-safety lookups of Pin…,
// Promote… and Rollback… (which release an environment runs, the previous default, the branch grouping, the next env).
import { ApiError } from '../../api/client';
import type { Job, Release, ReleasePin, ValidationIssue } from '../../api/types';
import { relativeTime } from '../../ui/time';

export const RELEASES_PAGE_SIZE = 50;
export const CATALOG_SIZE = 200;
export const JOBS_PAGE_SIZE = 50;

/** Cache keys under `game:<org>/<game>:…` so a game switch never shows another game's rows. */
export const keys = {
  releasesPrefix: (org: string, game: string) => `game:${org}/${game}:releases`,
  releasesList: (org: string, game: string, branch: string | null) => `game:${org}/${game}:releases:list?${branch ? `branch=${branch}` : 'all'}`,
  /** every branch's releases, newest first: the Pin select and the `#N` lookups */
  releasesCatalog: (org: string, game: string) => `game:${org}/${game}:releases:catalog`,
  manifest: (org: string, game: string, release: string) => `game:${org}/${game}:releases:manifest:${release}`,
  pins: (org: string, game: string, env: string) => `game:${org}/${game}:pins:${env}`,
  pinHistory: (org: string, game: string, env: string) => `game:${org}/${game}:pins:${env}:history`,
  pointer: (org: string, game: string, env: string) => `game:${org}/${game}:pointer:${env}`,
  jobs: (org: string, game: string) => `game:${org}/${game}:jobs`,
};

export interface DryRunResult {
  dryRun: true;
  releaseId?: string | null;
  commit?: string | null;
  contentHash?: string | null;
  files?: number | string | null;
  wouldUpload?: number | string | null;
  wouldReuse?: number | string | null;
  previousReleaseId?: string | null;
  changes?: { added?: string[]; changed?: string[]; removed?: string[]; unchanged?: number | string } | null;
  counts?: Record<string, unknown> | null;
}

export interface JobError {
  code?: string;
  message?: string;
  details?: unknown;
}

export const DONE = new Set(['succeeded', 'failed', 'cancelled']);

/** One status-tone table for every job badge (the Jobs tab and the build dialog agree): the design system's. */
export { JOB_TONE } from '../../ui/JobProgress';

export function asApiError(e: unknown): ApiError {
  return e instanceof ApiError ? e : new ApiError(0, 'unavailable', e instanceof Error ? e.message : String(e), '');
}

export function pretty(value: unknown): string {
  if (value === undefined) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** A failed build's `error.details` when they are validation issues (`{ code, severity, entity, message, … }[]`). */
export function issuesOf(error: unknown): ValidationIssue[] | null {
  const details = (error as JobError | null | undefined)?.details;
  if (!Array.isArray(details) || details.length === 0) return null;
  const ok = details.every((d) => d && typeof d === 'object' && typeof (d as ValidationIssue).code === 'string' && typeof (d as ValidationIssue).message === 'string' && typeof (d as ValidationIssue).entity === 'string');
  if (!ok) return null;
  return details.map((d) => {
    const i = d as Partial<ValidationIssue>;
    return { code: i.code!, severity: i.severity ?? 'error', entity: i.entity!, entityId: i.entityId ?? null, field: i.field ?? null, message: i.message! };
  });
}

/** The numeric entries of a release's (or a dry run's) `counts`. */
export function countEntries(counts: unknown): [string, number][] {
  if (!counts || typeof counts !== 'object' || Array.isArray(counts)) return [];
  const out: [string, number][] = [];
  for (const [k, v] of Object.entries(counts as Record<string, unknown>)) {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : null;
    if (n !== null) out.push([k, n]);
  }
  return out;
}

/** A real build's result carries the release: `{ release: {…} }` or the release itself. */
export function builtRelease(job: Job): Partial<Release> | null {
  const r = job.result as Record<string, unknown> | null | undefined;
  if (!r || typeof r !== 'object') return null;
  const inner = r.release;
  if (inner && typeof inner === 'object') return inner as Partial<Release>;
  return r as Partial<Release>;
}

export function shortSha(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 7) : '—';
}

/** `#12` when the release is in the loaded list, else its id. */
export function releaseName(releaseId: string, catalog: Release[] | undefined): string {
  const r = catalog?.find((x) => x.releaseId === releaseId);
  return r ? `#${r.number}` : releaseId;
}

export function rangeText(pin: Pick<ReleasePin, 'minAppVersion' | 'maxAppVersion'>): string {
  return `${pin.minAppVersion || '…'}–${pin.maxAppVersion || '…'}`;
}

export function pinState(pin: ReleasePin): 'active' | 'revoked' | 'superseded' {
  if (pin.revokedAt) return 'revoked';
  if (pin.supersededById) return 'superseded';
  return 'active';
}

// ---- deploy safety (spec/dashboard.md section 12.2: Pin…, Promote…, Rollback…; R-BR-04; R-ACC-01) -------------------------

/** The active default pin of an environment (neither revoked nor superseded), or null. */
export function activeDefaultPin(pins: ReleasePin[] | undefined): ReleasePin | null {
  return (pins ?? []).find((p) => p.isDefault && !p.revokedAt && !p.supersededById) ?? null;
}

/**
 * Which release an environment runs: the release of its active default pin, looked up in the game-wide catalog. Null when
 * nothing is pinned as the default, or when the pinned release is not in the loaded catalog.
 */
export function currentRelease(pins: ReleasePin[] | undefined, releases: Release[] | undefined): Release | null {
  const pin = activeDefaultPin(pins);
  if (!pin) return null;
  return releases?.find((r) => r.releaseId === pin.releaseId) ?? null;
}

/**
 * The previous default of an environment, from its pin history (read without `active`): the default pins newest first,
 * the first that is not the active default (superseded or revoked). Null when there is none.
 */
export function previousDefaultPin(history: ReleasePin[] | undefined): ReleasePin | null {
  const defaults = (history ?? []).filter((p) => p.isDefault).sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')));
  return defaults.find((p) => Boolean(p.revokedAt) || Boolean(p.supersededById)) ?? null;
}

/** The newest release of a branch (the catalog is newest first), or null. */
export function newestOfBranch(releases: Release[], branch: string | null | undefined): Release | null {
  if (!branch) return null;
  return releases.find((r) => r.branchName === branch) ?? null;
}

/** The catalog grouped per branch for the release selects: `firstBranch` first, then the others alphabetically. */
export function groupReleasesByBranch(releases: Release[], firstBranch: string | null | undefined): { branch: string; releases: Release[] }[] {
  const groups = new Map<string, Release[]>();
  for (const r of releases) {
    const list = groups.get(r.branchName) ?? [];
    list.push(r);
    groups.set(r.branchName, list);
  }
  const names = Array.from(groups.keys()).sort((a, b) => (a === firstBranch ? -1 : b === firstBranch ? 1 : a.localeCompare(b)));
  return names.map((branch) => ({ branch, releases: groups.get(branch)! }));
}

const KIND_ORDER = ['dev', 'stage', 'prod'] as const;

/** The environment a Promote… ships to: the first environment of the next kind in dev → stage → prod (skipping a missing kind). */
export function nextEnvironment<E extends { kind: string; slug: string }>(environments: E[], env: E): E | null {
  const at = KIND_ORDER.indexOf(env.kind as (typeof KIND_ORDER)[number]);
  if (at < 0) return null;
  for (const kind of KIND_ORDER.slice(at + 1)) {
    const found = environments.find((e) => e.kind === kind);
    if (found) return found;
  }
  return null;
}

/** "2 d ago" / "just now" for an instant: the shell's coarse relative time. */
export function agoText(iso: string | null | undefined, now = Date.now()): string {
  return relativeTime(iso, now);
}

export function sha8(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 8) : '—';
}
